import React from "react";

// Element -> colour (matches ChartView WUXING_COLOR)
const ELEM_HSL = {
  金: "hsl(45 65% 42%)",
  木: "hsl(150 42% 36%)",
  水: "hsl(210 55% 45%)",
  火: "hsl(6 70% 48%)",
  土: "hsl(30 46% 40%)",
};

// 8 compass directions + their governing element
const DIRS = [
  { key: "N", zh: "北", en: "N", el: "水", angle: 0 },
  { key: "NE", zh: "东北", en: "NE", el: "土", angle: 45 },
  { key: "E", zh: "东", en: "E", el: "木", angle: 90 },
  { key: "SE", zh: "东南", en: "SE", el: "木", angle: 135 },
  { key: "S", zh: "南", en: "S", el: "火", angle: 180 },
  { key: "SW", zh: "西南", en: "SW", el: "土", angle: 225 },
  { key: "W", zh: "西", en: "W", el: "金", angle: 270 },
  { key: "NW", zh: "西北", en: "NW", el: "金", angle: 315 },
];

const CX = 120;
const CY = 120;
const R_OUT = 110;
const R_IN = 40;

function polar(r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180; // 0deg = up (north), clockwise
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
}

function annularSector(startDeg, endDeg) {
  const [x0o, y0o] = polar(R_OUT, startDeg);
  const [x1o, y1o] = polar(R_OUT, endDeg);
  const [x1i, y1i] = polar(R_IN, endDeg);
  const [x0i, y0i] = polar(R_IN, startDeg);
  const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${x0o} ${y0o} A ${R_OUT} ${R_OUT} 0 ${large} 1 ${x1o} ${y1o} L ${x1i} ${y1i} A ${R_IN} ${R_IN} 0 ${large} 0 ${x0i} ${y0i} Z`;
}

// FengShuiCompass: highlights auspicious (喜用) and reduce (宜泄) directions.
export default function FengShuiCompass({ favorable = [], reduce = [], lang = "zh" }) {
  const fav = new Set(favorable || []);
  const red = new Set(reduce || []);
  const labelR = (R_OUT + R_IN) / 2;
  const centerAusp = fav.has("土");
  const centerReduce = red.has("土");

  return (
    <svg
      viewBox="0 0 240 240"
      className="w-full max-w-[240px] mx-auto"
      role="img"
      aria-label={lang === "zh" ? "个人开运方位罗盘" : "Personal lucky-direction compass"}
      data-testid="fengshui-compass"
    >
      {/* faint outer ring */}
      <circle cx={CX} cy={CY} r={R_OUT + 4} fill="none" stroke="hsl(var(--border))" strokeWidth="1" />

      {DIRS.map((d) => {
        const color = ELEM_HSL[d.el];
        const isFav = fav.has(d.el);
        const isRed = red.has(d.el);
        const [lx, ly] = polar(labelR, d.angle);
        let fill = "hsl(var(--secondary))";
        let fillOpacity = 0.5;
        let stroke = "hsl(var(--border))";
        let strokeWidth = 1;
        let dash = "0";
        if (isFav) {
          fill = color;
          fillOpacity = 0.2;
          stroke = color;
          strokeWidth = 2.5;
        } else if (isRed) {
          fill = "hsl(var(--muted))";
          fillOpacity = 0.6;
          stroke = "hsl(var(--muted-foreground))";
          strokeWidth = 1.25;
          dash = "3 3";
        }
        return (
          <g key={d.key}>
            <path
              d={annularSector(d.angle - 22.5, d.angle + 22.5)}
              fill={fill}
              fillOpacity={fillOpacity}
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeDasharray={dash}
            />
            <text
              x={lx}
              y={ly - (isFav ? 5 : 0)}
              textAnchor="middle"
              dominantBaseline="middle"
              className="font-display"
              style={{
                fontSize: d.key.length === 1 || d.zh.length === 1 ? 15 : 13,
                fontWeight: isFav ? 700 : 500,
                fill: isFav ? color : "hsl(var(--muted-foreground))",
              }}
            >
              {lang === "zh" ? d.zh : d.en}
            </text>
            {isFav && (
              <text
                x={lx}
                y={ly + 11}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 11, fontWeight: 700, fill: color }}
              >
                {d.el}
              </text>
            )}
          </g>
        );
      })}

      {/* centre (中宫 / 土) */}
      <circle
        cx={CX}
        cy={CY}
        r={R_IN - 4}
        fill={centerAusp ? ELEM_HSL["土"] : "hsl(var(--card))"}
        fillOpacity={centerAusp ? 0.2 : 1}
        stroke={centerAusp ? ELEM_HSL["土"] : centerReduce ? "hsl(var(--muted-foreground))" : "hsl(var(--border))"}
        strokeWidth={centerAusp ? 2.5 : 1.25}
        strokeDasharray={centerReduce && !centerAusp ? "3 3" : "0"}
      />
      <text
        x={CX}
        y={CY}
        textAnchor="middle"
        dominantBaseline="middle"
        className="font-display"
        style={{ fontSize: 16, fontWeight: 700, fill: centerAusp ? ELEM_HSL["土"] : "hsl(var(--brand-ink))" }}
      >
        {lang === "zh" ? "中" : "C"}
      </text>

      {/* north pointer */}
      <polygon points={`${CX},6 ${CX - 6},20 ${CX + 6},20`} fill="hsl(var(--brand-vermillion))" />
    </svg>
  );
}
